import React, { Fragment, type ElementType, type ReactNode } from "react";

import clsx from "clsx";

import {
  Menu,
  MenuItems,
  MenuItem,
  Transition,
  type MenuItemProps,
} from "@headlessui/react";

const menuPositionClasses = {
  "top-left": "top end",
  "top-right": "top start",
  "bottom-left": "bottom end",
  "bottom-right": "bottom start",
} as const;

interface ContextMenuProps {
  menuButton: ReactNode;
  className?: string;
  menuClassName?: string;
  children: ReactNode;
  menuPosition?: keyof typeof menuPositionClasses;
}

export const ContextMenu = ({
  className,
  menuButton,
  menuClassName,
  children,
  menuPosition = "bottom-left",
}: ContextMenuProps) => {
  return (
    <Menu as="div" className={className}>
      {menuButton}
      <Transition
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <MenuItems
          className={clsx(
            menuClassName,
            "z-[999] rounded-md bg-white text-left text-sm elevation-1",
          )}
          anchor={menuPositionClasses[menuPosition]}
        >
          {children}
        </MenuItems>
      </Transition>
    </Menu>
  );
};

ContextMenu.Item = function <T extends ElementType>({
  children,
  activeClassname = "bg-cosmic-4",
  ...props
}: Omit<MenuItemProps<T>, "children"> & {
  children: ReactNode;
  className?: string;
  activeClassname?: string;
}) {
  const { as, ...rest } = props;
  const Component = as || Fragment;

  return (
    <MenuItem>
      {({ active }) => {
        return (
          <Component
            {...rest}
            className={clsx(props.className, { [activeClassname]: active })}
          >
            {children}
          </Component>
        );
      }}
    </MenuItem>
  );
};
ContextMenu.Button = Menu.Button;
